import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/hooks/helpers'
import { BreakdownTable, BreakdownTableSkeleton, Tooltip } from '@/ui'
import {
	CalculatorIcon,
	ChartBarIcon,
	CurrencyEuroIcon,
	EyeIcon,
	InformationCircleIcon,
	MegaphoneIcon,
	UserGroupIcon,
	UserIcon,
} from '@heroicons/react/24/outline'
import type { OrganisationAnalyticsShowResponse } from '@jobilla/nexus-sdk'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'

export type OrganisationAnalyticsViewProps = {
	data: OrganisationAnalyticsShowResponse | undefined
	isLoading: boolean
	isError: boolean
	onRetry: () => void
	onPageChange: (page: number) => void
	filtersSlot?: React.ReactNode
}

export function OrganisationAnalyticsView({
	data,
	isLoading,
	isError,
	onRetry,
	onPageChange,
	filtersSlot,
}: OrganisationAnalyticsViewProps) {
	const { t } = useTranslation('analytics')

	if (isError && !data) {
		return (
			<div className="px-8 py-6">
				<div className="bg-white border border-grey-200 rounded-lg flex items-center justify-center min-h-[400px]">
					<div className="flex flex-col items-center gap-4 max-w-[350px] text-center">
						<div className="scale-200 text-red-400">
							<ChartBarIcon className="size-6" />
						</div>
						<div className="text-sm font-semibold text-grey-700">
							{t('organisationAnalytics.error.title')}
						</div>
						<div className="text-sm text-grey-500">
							{t('organisationAnalytics.error.description')}
						</div>
						<Button
							onClick={onRetry}
							className="bg-brand-600 text-white hover:bg-brand-700"
						>
							{t('organisationAnalytics.error.retry')}
						</Button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="px-8 py-6 space-y-8">
			<h1 className="text-2xl font-semibold tracking-tight text-grey-900">
				{t('organisationAnalytics.title')}
			</h1>
			{filtersSlot}
			<div>
				<h2 className="text-xs uppercase tracking-widest font-semibold text-grey-500 mb-4">
					{t('organisationAnalytics.summary.heading')}
				</h2>
				{isLoading
					? <MetricCardsSkeleton />
					: data && <MetricCards summary={data.summary} />}
			</div>

			<div>
				<h2 className="text-xs uppercase tracking-widest font-semibold text-grey-500 mb-4">
					{t('organisationAnalytics.breakdown.title')}
				</h2>
				{isLoading
					? <BreakdownTableSkeleton />
					: data && data.breakdown.meta.total === 0
					? <EmptyBreakdown />
					: data && (
						<BreakdownTable
							rows={data.breakdown.data}
							meta={data.breakdown.meta}
							onPageChange={onPageChange}
						/>
					)}
			</div>
		</div>
	)
}

function MetricCards({ summary }: { summary: OrganisationAnalyticsShowResponse['summary'] }) {
	const { t } = useTranslation('analytics')
	const ts = (key: string) => t(`organisationAnalytics.summary.${key}`)

	return (
		<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
			<MetricCard
				label={ts('totalCampaigns')}
				value={summary.totalCampaigns.toLocaleString()}
				icon={<MegaphoneIcon className="size-6" />}
				color="blue"
				delay={0}
			/>
			<MetricCard
				label={ts('totalCandidates')}
				value={summary.totalCandidates.toLocaleString()}
				icon={<UserIcon className="size-6" />}
				color="purple"
				delay={0.05}
			/>
			<MetricCard
				label={ts('totalHires')}
				value={summary.totalHires.toLocaleString()}
				tooltip={ts('totalHiresTooltip')}
				icon={<UserGroupIcon className="size-6" />}
				color="green"
				delay={0.1}
			/>
			<MetricCard
				label={ts('totalBudget')}
				value={formatCurrency(summary.totalBudget / 100)}
				icon={<CurrencyEuroIcon className="size-6" />}
				color="blue"
				delay={0.15}
			/>
			<MetricCard
				label={ts('avgBudgetPerCandidate')}
				value={summary.avgBudgetPerCandidate !== null
					? formatCurrency(summary.avgBudgetPerCandidate / 100)
					: ts('notAvailable')}
				icon={<CalculatorIcon className="size-6" />}
				color="purple"
				delay={0.2}
			/>
			<MetricCard
				label={ts('avgPricePerHire')}
				value={summary.avgPricePerHire !== null
					? formatCurrency(summary.avgPricePerHire / 100)
					: ts('notAvailable')}
				tooltip={ts('avgPricePerHireTooltip')}
				icon={<CalculatorIcon className="size-6" />}
				color="green"
				delay={0.25}
			/>
			<MetricCard
				label={ts('totalViews')}
				value={summary.totalViews.toLocaleString()}
				icon={<EyeIcon className="size-6" />}
				color="blue"
				delay={0.3}
			/>
		</div>
	)
}

function MetricCard({
	label,
	value,
	tooltip,
	icon,
	color,
	delay = 0,
}: {
	label: string
	value: string | number
	tooltip?: string
	icon: React.ReactNode
	color: 'purple' | 'green' | 'blue'
	delay?: number
}) {
	const iconClasses = {
		purple: 'bg-gradient-to-br from-purple-500 to-purple-600 text-white',
		green: 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white',
		blue: 'bg-gradient-to-br from-sky-500 to-sky-600 text-white',
	}

	const accentClasses = {
		purple: 'bg-purple-500',
		green: 'bg-emerald-500',
		blue: 'bg-sky-500',
	}

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2, delay }}
			className="relative overflow-hidden p-8 rounded-xl border border-grey-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
		>
			<div className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${accentClasses[color]}`} />
			<div className="relative">
				<div className="flex items-start justify-between mb-6">
					{tooltip
						? (
							<Tooltip content={tooltip} className="max-w-xs leading-relaxed">
								<div
									tabIndex={0}
									className="flex items-center gap-1.5 cursor-help rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-grey-400"
								>
									<div className="text-xs font-semibold text-grey-500 uppercase tracking-widest">
										{label}
									</div>
									<InformationCircleIcon className="size-3.5 text-grey-400" />
								</div>
							</Tooltip>
						)
						: <div className="text-xs font-semibold text-grey-500 uppercase tracking-widest">{label}</div>}
					<div className={`p-3 rounded-xl ${iconClasses[color]} shadow-md`}>
						{icon}
					</div>
				</div>
				<div className="text-4xl font-bold tracking-tight text-grey-900 mb-2">
					{value}
				</div>
			</div>
		</motion.div>
	)
}

function MetricCardsSkeleton() {
	return (
		<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
			{Array.from({ length: 7 }).map((_, i) => (
				<div key={i} className="h-[148px] rounded-xl border border-grey-200 bg-white animate-pulse" />
			))}
		</div>
	)
}

function EmptyBreakdown() {
	const { t } = useTranslation('analytics')

	return (
		<div className="bg-white border border-grey-200 rounded-lg flex items-center justify-center py-16">
			<div className="flex flex-col items-center gap-4 max-w-[350px] text-center">
				<div className="scale-200 text-grey-300">
					<ChartBarIcon className="size-6" />
				</div>
				<div className="text-sm font-semibold text-grey-500">
					{t('organisationAnalytics.breakdown.noCampaigns')}
				</div>
				<div className="text-sm text-grey-400">
					{t('organisationAnalytics.breakdown.noCampaignsDescription')}
				</div>
			</div>
		</div>
	)
}
